import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
	Box,
	Container,
	Typography,
	Card,
	CardContent,
	CircularProgress,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	Paper,
	Stack,
	Chip,
	Button,
	useTheme,
	alpha,
} from "@mui/material";
import {
	Person,
	Phone,
	CalendarToday,
	AutoAwesome,
	HotelClass,
	Comment,
	Chat,
	AccountCircle,
	ExpandMore,
} from "@mui/icons-material";
import {
	WhatshotTwoTone as HotIcon,
	AcUnitTwoTone as ColdIcon,
	BalanceTwoTone as NeutralIcon,
} from "@mui/icons-material";
import { getPublicLeadView } from "../utils/api";
import { getDocumentType } from "../utils/document";
import { splitMessageContent } from "../utils/messageUtils";

/** Verde primary do projeto (LIA) */
const PRIMARY_GREEN = "#13B497";

const getStatusColor = (status: string): string => {
	switch (status?.toLowerCase()) {
		case "novo":
			return "#2196f3";
		case "contato_inicial":
			return "#ff9800";
		case "qualificado":
			return "#4caf50";
		case "proposta":
			return "#9c27b0";
		case "negociacao":
			return "#ff5722";
		case "fechado":
			return "#4caf50";
		case "perdido":
			return "#f44336";
		default:
			return "#757575";
	}
};

function getWhatsAppUrl(phoneNumber: string): string {
	const digits = phoneNumber.replace(/\D/g, "");
	const withCountry = digits.length >= 10 && !digits.startsWith("55") ? `55${digits}` : digits;
	return `https://wa.me/${withCountry}`;
}

const getStatusLabel = (status: string): string => {
	const labels: Record<string, string> = {
		novo: "Novo",
		contato_inicial: "Contato Inicial",
		qualificado: "Qualificado",
		proposta: "Proposta",
		negociacao: "Negociação",
		fechado: "Fechado",
		perdido: "Perdido",
	};
	return labels[status?.toLowerCase()] || status || "Novo";
};

const RatingGridItem = ({ title, value }: { title: string; value: string }) => (
	<Box sx={{ mb: 2 }}>
		<Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
			{title}
		</Typography>
		<Typography variant="body2" sx={{ fontWeight: 500 }}>
			{value || "N/A"}
		</Typography>
	</Box>
);

export default function PublicLeadViewPage() {
	const { organizationId, leadId } = useParams<{ organizationId: string; leadId: string }>();
	const theme = useTheme();
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [data, setData] = useState<Awaited<ReturnType<typeof getPublicLeadView>> | null>(null);

	useEffect(() => {
		if (!organizationId || !leadId) {
			setError("Parâmetros inválidos na URL.");
			setLoading(false);
			return;
		}

		getPublicLeadView(organizationId, leadId)
			.then(setData)
			.catch((err) => {
				setError(err?.message || "Lead não encontrado.");
			})
			.finally(() => setLoading(false));
	}, [organizationId, leadId]);

	if (loading) {
		return (
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					minHeight: "60vh",
				}}
			>
				<CircularProgress sx={{ color: PRIMARY_GREEN }} />
			</Box>
		);
	}

	if (error || !data) {
		return (
			<Container maxWidth="md" sx={{ py: 4 }}>
				<Paper sx={{ p: 4, textAlign: "center" }}>
					<Typography variant="h6" color="error">
						{error || "Lead não encontrado."}
					</Typography>
					<Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
						Verifique se o link está correto e tente novamente.
					</Typography>
				</Paper>
			</Container>
		);
	}

	const { lead, chatHistory } = data;
	const statusColor =
		(lead as { statusColor?: string }).statusColor || getStatusColor(lead.status || "novo");
	const statusName =
		(lead as { statusName?: string }).statusName || getStatusLabel(lead.status || "novo");
	const accentColor = PRIMARY_GREEN;
	const createdDate = lead.creationTime
		? new Date(lead.creationTime).toLocaleString("pt-BR", {
				day: "2-digit",
				month: "2-digit",
				year: "numeric",
				hour: "2-digit",
				minute: "2-digit",
		  })
		: "-";

	const observacoes = lead.observacoes || [];
	const messages = chatHistory?.messages || [];

	return (
		<Container maxWidth="md" sx={{ py: 4 }}>
			<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2, mb: 3 }}>
				<Typography variant="h5" sx={{ fontWeight: 600, color: accentColor }}>
					Detalhes do Lead
				</Typography>
				<Button
					variant="contained"
					href={getWhatsAppUrl(lead.phoneNumber)}
					target="_blank"
					rel="noopener noreferrer"
					sx={{
						backgroundColor: "#25d366",
						color: "#fff",
						"&:hover": { backgroundColor: "#20ba5c" },
						textTransform: "none",
					}}
					startIcon={
						<Box component="span" sx={{ display: "inline-flex", alignItems: "center" }}>
							<svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
								<path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
							</svg>
						</Box>
					}
				>
					Abrir WhatsApp
				</Button>
			</Box>

			{/* Informações principais */}
			<Card sx={{ mb: 2, border: `1px solid ${alpha(accentColor, 0.3)}` }}>
				<CardContent sx={{ p: 2 }}>
					<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
						<Typography variant="subtitle1" sx={{ fontWeight: 600, color: accentColor }}>
							Informações gerais
						</Typography>
						<Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
							{lead.leadQuality === "Quente" && <HotIcon sx={{ fontSize: 20, color: "#e53935" }} />}
							{lead.leadQuality === "Neutro" && <NeutralIcon sx={{ fontSize: 20, color: "#fb8c00" }} />}
							{lead.leadQuality === "Frio" && <ColdIcon sx={{ fontSize: 20, color: "#039be5" }} />}
							<Chip
								label={statusName}
								size="small"
								sx={{
									background: theme.palette.mode === "dark" ? "#313131" : "#f2f2f2",
									color: statusColor,
									fontWeight: 500,
								}}
							/>
						</Box>
					</Box>

					<Stack spacing={2}>
						<Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
							<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
								<Person sx={{ fontSize: 18, color: "text.secondary" }} />
								<Box>
									<Typography variant="caption" color="text.secondary">
										Nome
									</Typography>
									<Typography variant="body2" sx={{ fontWeight: 500 }}>
										{lead.name}
									</Typography>
								</Box>
							</Box>
							<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
								<Phone sx={{ fontSize: 18, color: "text.secondary" }} />
								<Box>
									<Typography variant="caption" color="text.secondary">
										Telefone
									</Typography>
									<Typography variant="body2" sx={{ fontWeight: 500 }}>
										{lead.phoneNumber}
									</Typography>
								</Box>
							</Box>
							{lead.email && (
								<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
									<Person sx={{ fontSize: 18, color: "text.secondary" }} />
									<Box>
										<Typography variant="caption" color="text.secondary">
											E-mail
										</Typography>
										<Typography variant="body2" sx={{ fontWeight: 500 }}>
											{lead.email}
										</Typography>
									</Box>
								</Box>
							)}
						</Box>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
							<CalendarToday sx={{ fontSize: 18, color: "text.secondary" }} />
							<Box>
								<Typography variant="caption" color="text.secondary">
									Data de criação
								</Typography>
								<Typography variant="body2" sx={{ fontWeight: 500 }}>
									{createdDate}
								</Typography>
							</Box>
						</Box>
					</Stack>
				</CardContent>
			</Card>

			{/* Resumo da IA */}
			{lead.resumeAI && (
				<Accordion sx={{ mb: 1 }}>
					<AccordionSummary expandIcon={<ExpandMore />} sx={{ minHeight: "48px" }}>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
							<AutoAwesome sx={{ fontSize: 20, color: accentColor }} />
							<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
								Resumo da IA
							</Typography>
						</Box>
					</AccordionSummary>
					<AccordionDetails sx={{ pt: 0 }}>
						<Typography
							variant="body2"
							sx={{
								lineHeight: 1.8,
								whiteSpace: "pre-wrap",
								wordBreak: "break-word",
								color: "text.secondary",
							}}
						>
							{lead.resumeAI}
						</Typography>
					</AccordionDetails>
				</Accordion>
			)}

			{/* Rating */}
			{lead.rating && (
				<Accordion sx={{ mb: 1 }}>
					<AccordionSummary expandIcon={<ExpandMore />} sx={{ minHeight: "48px" }}>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
							<HotelClass sx={{ fontSize: 20, color: accentColor }} />
							<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
								Avaliação
							</Typography>
							{[...new Array(lead.rating?.businessPotencial || 0)].map((_, index) => (
								<Box key={index} component="span" sx={{ color: "#ffc107", fontSize: 20 }}>
									★
								</Box>
							))}
						</Box>
					</AccordionSummary>
					<AccordionDetails sx={{ pt: 0 }}>
						<Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 2 }}>
							<RatingGridItem
								title="Idade"
								value={lead.rating?.age?.toString() || ""}
							/>
							<RatingGridItem
								title="Gênero"
								value={lead.rating?.gender || ""}
							/>
							<RatingGridItem
								title="Tipo de documento"
								value={getDocumentType(lead.rating?.document || "") || "N/A"}
							/>
							<RatingGridItem
								title="Documento"
								value={lead.rating?.document || ""}
							/>
							<RatingGridItem
								title="Score de crédito"
								value={lead.rating?.creditScoreD00 || ""}
							/>
							<RatingGridItem
								title={
									getDocumentType(lead.rating?.document || "") === "Pessoa Física"
										? "Renda pessoal"
										: "Receita compartilhada"
								}
								value={
									getDocumentType(lead.rating?.document || "") === "Pessoa Física"
										? lead.rating?.incomePersonal || ""
										: lead.rating?.revenueShared || ""
								}
							/>
							<RatingGridItem
								title={
									getDocumentType(lead.rating?.document || "") === "Pessoa Física"
										? "Renda do parceiro"
										: "Balanço da empresa"
								}
								value={
									getDocumentType(lead.rating?.document || "") === "Pessoa Física"
										? lead.rating?.incomePartner || ""
										: lead.rating?.revenueBalance || ""
								}
							/>
							<RatingGridItem
								title={
									getDocumentType(lead.rating?.document || "") === "Pessoa Física"
										? "Renda familiar"
										: "Receita presumida"
								}
								value={
									getDocumentType(lead.rating?.document || "") === "Pessoa Física"
										? lead.rating?.incomeFamily || ""
										: lead.rating?.revenuePresumed || ""
								}
							/>
						</Box>
					</AccordionDetails>
				</Accordion>
			)}

			{/* Observações */}
			<Accordion sx={{ mb: 1 }}>
				<AccordionSummary expandIcon={<ExpandMore />} sx={{ minHeight: "48px" }}>
					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						<Comment sx={{ fontSize: 20, color: accentColor }} />
						<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
							Observações ({observacoes.length})
						</Typography>
					</Box>
				</AccordionSummary>
				<AccordionDetails sx={{ pt: 0 }}>
					{observacoes.length === 0 ? (
						<Typography
							variant="body2"
							color="text.secondary"
							sx={{ textAlign: "center", py: 2 }}
						>
							Nenhuma observação.
						</Typography>
					) : (
						<Stack spacing={1}>
							{[...observacoes]
								.sort(
									(a, b) =>
										new Date((b as { dataContato?: string }).dataContato || 0).getTime() -
										new Date((a as { dataContato?: string }).dataContato || 0).getTime()
								)
								.map((obs: { id?: string; _id?: string; comentario?: string; dataContato?: string }) => (
									<Paper
										key={obs.id || obs._id || Math.random()}
										sx={{
											p: 1.5,
											backgroundColor: alpha(accentColor, 0.08),
											border: `1px solid ${alpha(accentColor, 0.2)}`,
										}}
									>
										<Typography
											variant="caption"
											color="text.secondary"
											sx={{ display: "block", mb: 0.5 }}
										>
											{obs.dataContato
												? new Date(obs.dataContato).toLocaleString("pt-BR")
												: "-"}
										</Typography>
										<Typography variant="body2">
											{obs.comentario || ""}
										</Typography>
									</Paper>
								))}
						</Stack>
					)}
				</AccordionDetails>
			</Accordion>

			{/* Histórico de conversas */}
			{messages.length > 0 && (
				<Accordion defaultExpanded>
					<AccordionSummary expandIcon={<ExpandMore />} sx={{ minHeight: "48px" }}>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
							<Chat sx={{ fontSize: 20, color: accentColor }} />
							<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
								Histórico de conversas ({messages.length})
							</Typography>
						</Box>
					</AccordionSummary>
					<AccordionDetails sx={{ pt: 0 }}>
						<Box
							sx={{
								maxHeight: 450,
								padding: 2,
								overflowY: "auto",
								background:
									theme.palette.mode === "dark"
										? "#1a1a1a"
										: "url('/whatsapp-background.jpg')",
								backgroundRepeat: "repeat",
								backgroundSize: theme.palette.mode === "dark" ? "auto" : "400px auto",
								borderRadius: "12px",
								border: `1px solid ${
									theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.06)" : "transparent"
								}`,
							}}
						>
							{messages.map((message, index) => {
								let messageParts;
								try {
									messageParts = splitMessageContent(message.content || "");
								} catch {
									messageParts = [{ text: message.content || "", media: [] }];
								}
								return (
									<Box key={index} sx={{ mb: 2.5 }}>
										<Box
											sx={{
												display: "flex",
												justifyContent:
													message.role === "assistant" ? "flex-start" : "flex-end",
												mb: 1,
											}}
										>
											<Paper
												sx={{
													p: 2.5,
													maxWidth: "75%",
													backgroundColor:
														message.role === "assistant"
															? theme.palette.mode === "dark"
																? "rgba(101, 240, 197, 0.12)"
																: "rgba(19, 180, 151, 0.08)"
															: theme.palette.mode === "dark"
																? "rgba(255,255,255,0.05)"
																: "#f3f4f6",
													border: `1px solid ${
														message.role === "assistant"
															? theme.palette.mode === "dark"
																? "rgba(101, 240, 197, 0.4)"
																: "#13B497"
															: accentColor
													}`,
													borderRadius:
														message.role === "assistant"
															? "20px 20px 20px 5px"
															: "20px 20px 5px 20px",
													boxShadow: 1,
												}}
											>
												<Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
													{message.role === "assistant" ? (
														<AutoAwesome
															sx={{
																mr: 1.5,
																fontSize: 18,
																color:
																	theme.palette.mode === "dark" ? "#65f0c5" : PRIMARY_GREEN,
															}}
														/>
													) : (
														<AccountCircle
															sx={{
																mr: 1.5,
																fontSize: 18,
																color: accentColor,
															}}
														/>
													)}
													<Typography
														variant="caption"
														sx={{
															fontWeight: "bold",
															color:
																message.role === "assistant"
																	? theme.palette.mode === "dark"
																		? "#65f0c5"
																		: PRIMARY_GREEN
																	: accentColor,
														}}
													>
														{message.role === "assistant"
															? "🤖 Assistente IA"
															: `👤 ${lead.name}`}
													</Typography>
												</Box>
												{messageParts.map((part, partIndex) => (
													<Box key={partIndex}>
														{part.text ? (
															<Typography
																variant="body1"
																sx={{
																	whiteSpace: "pre-wrap",
																	lineHeight: 1.6,
																	fontSize: "0.95rem",
																	wordBreak: "break-word",
																	mb: part.media.length > 0 ? 1.5 : 0,
																}}
															>
																{part.text}
															</Typography>
														) : null}
														{part.media.length > 0 ? (
															<Stack spacing={1} sx={{ mt: part.text ? 1 : 0 }}>
																{part.media.map((media, mediaIndex) =>
																	media.type === "image" ? (
																		<Box
																			key={mediaIndex}
																			component="img"
																			src={media.url}
																			alt={media.alt || "Imagem"}
																			sx={{
																				maxWidth: "100%",
																				maxHeight: 280,
																				borderRadius: "12px",
																				objectFit: "cover",
																				cursor: "pointer",
																				boxShadow:
																					theme.palette.mode === "dark"
																						? "0 2px 8px rgba(0,0,0,0.4)"
																						: "0 2px 8px rgba(0,0,0,0.1)",
																				"&:hover": { opacity: 0.92 },
																			}}
																			onClick={() => window.open(media.url, "_blank")}
																		/>
																	) : (
																		<Box
																			key={mediaIndex}
																			component="video"
																			src={media.url}
																			controls
																			sx={{
																				maxWidth: "100%",
																				maxHeight: 280,
																				borderRadius: "12px",
																				boxShadow:
																					theme.palette.mode === "dark"
																						? "0 2px 8px rgba(0,0,0,0.4)"
																						: "0 2px 8px rgba(0,0,0,0.1)",
																			}}
																		/>
																	)
																)}
															</Stack>
														) : null}
													</Box>
												))}
												<Typography
													variant="caption"
													color="text.secondary"
													sx={{ mt: 1.5, display: "block", fontStyle: "italic" }}
												>
													{message.timestamp
														? new Date(new Date(message.timestamp).getTime() + 3 * 60 * 60 * 1000).toLocaleString("pt-BR", {
																day: "2-digit",
																month: "2-digit",
																year: "numeric",
																hour: "2-digit",
																minute: "2-digit",
														  })
														: ""}
												</Typography>
											</Paper>
										</Box>
									</Box>
								);
							})}
						</Box>
					</AccordionDetails>
				</Accordion>
			)}
		</Container>
	);
}
